/**
 * Caractère gravé sur le clavier de l'utilisateur pour un emplacement physique
 * (KeyboardEvent.code) : `navigator.keyboard.getLayoutMap()` quand le navigateur le permet,
 * sinon le caractère reçu à la dernière frappe sur cette touche, sinon celui d'un QWERTY.
 */
import { useEffect, useState } from 'react';

const QWERTY: Record<string, string> = {
  Semicolon: ';',
  Quote: "'",
  Backslash: '\\',
  BracketLeft: '[',
  BracketRight: ']',
  Comma: ',',
  Period: '.',
  Slash: '/',
  Minus: '-',
  Equal: '=',
  Backquote: '`',
  IntlBackslash: '<',
  Space: 'Espace',
  Enter: 'Entrée',
  Escape: 'Échap',
  Tab: 'Tab',
};

const observes = new Map<string, string>();
let disposition: Map<string, string> | null = null;
const abonnes = new Set<() => void>();

function prevenir() {
  for (const f of abonnes) f();
}

function nomParDefaut(code: string): string {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  return QWERTY[code] ?? code;
}

/** Caractère affiché pour une touche. */
export function caractereDeLaTouche(code: string): string {
  if (QWERTY[code] && /^(Space|Enter|Escape|Tab)$/.test(code)) return QWERTY[code];
  const c = disposition?.get(code) ?? observes.get(code) ?? nomParDefaut(code);
  return c.length === 1 ? c.toLocaleUpperCase('fr') : c;
}

let initialise = false;
function initialiser() {
  if (initialise || typeof window === 'undefined') return;
  initialise = true;
  const clavier = (
    navigator as Navigator & { keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> } }
  ).keyboard;
  void clavier
    ?.getLayoutMap?.()
    .then((carte) => {
      disposition = new Map(carte);
      prevenir();
    })
    .catch(() => undefined);
  window.addEventListener(
    'keydown',
    (e) => {
      if (
        e.key.length === 1 &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        observes.get(e.code) !== e.key
      ) {
        observes.set(e.code, e.key);
        if (!disposition) prevenir();
      }
    },
    true,
  );
}

/** Rafraîchit l'écran quand la disposition du clavier devient connue. */
export function useDispositionClavier(): (code: string) => string {
  const [, setVersion] = useState(0);
  useEffect(() => {
    initialiser();
    const f = () => setVersion((v) => v + 1);
    abonnes.add(f);
    return () => {
      abonnes.delete(f);
    };
  }, []);
  return caractereDeLaTouche;
}
